import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import { 
  Users, Mail, Building2, HardHat, Briefcase, Download, Eye, Calendar, Phone, FileText, ArrowLeft,
  Plus, Edit, Trash2, X, Save, Loader2, LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { clearSession, getAdminEmail } from '@/lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Modal Component
function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4 shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-[#1A1A2E]">
          <h2 className="text-lg font-bold text-white">{title}</h2>
          <button onClick={onClose} className="text-white hover:text-[#E94560]"><X size={24} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// Job Offer Form
function JobOfferForm({ offer, onSave, onCancel }) {
  const [formData, setFormData] = useState(offer || {
    title: '', location: '', contract_type: 'Tempo indeterminato', experience_required: 'Mid-level',
    salary_range: '', description: '', requirements: '', company: '', is_active: true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (offer?.id) {
        await axios.put(`${API}/job-offers/${offer.id}`, formData);
        toast.success('Offerta aggiornata!');
      } else {
        await axios.post(`${API}/job-offers`, formData);
        toast.success('Offerta creata!');
      }
      onSave();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Titolo *</label>
        <input type="text" required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})}
          className="input-industrial w-full" placeholder="es. Project Manager Senior" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Località *</label>
          <input type="text" required value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})}
            className="input-industrial w-full" placeholder="es. Milano, Italia" />
        </div>
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Azienda (opzionale)</label>
          <input type="text" value={formData.company || ''} onChange={e => setFormData({...formData, company: e.target.value})}
            className="input-industrial w-full" placeholder="es. Cliente XYZ" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Tipo contratto *</label>
          <select value={formData.contract_type} onChange={e => setFormData({...formData, contract_type: e.target.value})}
            className="input-industrial w-full">
            <option>Tempo indeterminato</option>
            <option>Tempo determinato</option>
            <option>Partita IVA</option>
            <option>Collaborazione</option>
          </select>
        </div>
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Esperienza richiesta *</label>
          <select value={formData.experience_required} onChange={e => setFormData({...formData, experience_required: e.target.value})}
            className="input-industrial w-full">
            <option>Junior (0-2 anni)</option>
            <option>Mid-level (3-5 anni)</option>
            <option>Senior (5+ anni)</option>
            <option>Expert (10+ anni)</option>
          </select>
        </div>
      </div>
      <div>
        <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Range salariale (opzionale)</label>
        <input type="text" value={formData.salary_range || ''} onChange={e => setFormData({...formData, salary_range: e.target.value})}
          className="input-industrial w-full" placeholder="es. €50.000 - €70.000" />
      </div>
      <div>
        <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Descrizione *</label>
        <textarea required value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
          className="input-industrial w-full h-32 resize-none" placeholder="Descrivi il ruolo e le responsabilità..." />
      </div>
      <div>
        <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Requisiti *</label>
        <textarea required value={formData.requirements} onChange={e => setFormData({...formData, requirements: e.target.value})}
          className="input-industrial w-full h-32 resize-none" placeholder="Elenca i requisiti richiesti..." />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="is_active" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
        <label htmlFor="is_active" className="text-sm text-[#555555]">Offerta attiva (visibile sul sito)</label>
      </div>
      <div className="flex gap-4 pt-4">
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          {offer?.id ? 'Aggiorna' : 'Crea'} offerta
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">Annulla</button>
      </div>
    </form>
  );
}

// Subcontract Request Form
function SubcontractForm({ request, onSave, onCancel }) {
  const [formData, setFormData] = useState(request || {
    title: '', project_name: '', location: '', specialization: 'Carpenteria metallica',
    start_date: '', duration: '', budget_range: '', description: '', requirements: '', is_active: true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (request?.id) {
        await axios.put(`${API}/subcontract-requests/${request.id}`, formData);
        toast.success('Richiesta aggiornata!');
      } else {
        await axios.post(`${API}/subcontract-requests`, formData);
        toast.success('Richiesta creata!');
      }
      onSave();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Titolo *</label>
        <input type="text" required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})}
          className="input-industrial w-full" placeholder="es. Carpenteria metallica impianto X" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Nome progetto *</label>
          <input type="text" required value={formData.project_name} onChange={e => setFormData({...formData, project_name: e.target.value})}
            className="input-industrial w-full" placeholder="es. Impianto Fotovoltaico ABC" />
        </div>
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Località *</label>
          <input type="text" required value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})}
            className="input-industrial w-full" placeholder="es. Ravenna, Italia" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Specializzazione *</label>
          <select value={formData.specialization} onChange={e => setFormData({...formData, specialization: e.target.value})}
            className="input-industrial w-full">
            <option>Carpenteria metallica</option>
            <option>Montaggi meccanici</option>
            <option>Impianti elettrici</option>
            <option>Saldature certificate</option>
            <option>Manutenzione</option>
            <option>Altro</option>
          </select>
        </div>
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Data inizio *</label>
          <input type="text" required value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})}
            className="input-industrial w-full" placeholder="es. Marzo 2026" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Durata *</label>
          <input type="text" required value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})}
            className="input-industrial w-full" placeholder="es. 6 mesi" />
        </div>
        <div>
          <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Budget (opzionale)</label>
          <input type="text" value={formData.budget_range || ''} onChange={e => setFormData({...formData, budget_range: e.target.value})}
            className="input-industrial w-full" placeholder="es. €100.000 - €150.000" />
        </div>
      </div>
      <div>
        <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Descrizione *</label>
        <textarea required value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
          className="input-industrial w-full h-32 resize-none" placeholder="Descrivi il progetto e le attività..." />
      </div>
      <div>
        <label className="font-bold text-sm text-[#1A1A2E] block mb-1">Requisiti *</label>
        <textarea required value={formData.requirements} onChange={e => setFormData({...formData, requirements: e.target.value})}
          className="input-industrial w-full h-32 resize-none" placeholder="Elenca i requisiti per l'impresa..." />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="is_active_sub" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
        <label htmlFor="is_active_sub" className="text-sm text-[#555555]">Richiesta attiva (visibile sul sito)</label>
      </div>
      <div className="flex gap-4 pt-4">
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          {request?.id ? 'Aggiorna' : 'Crea'} richiesta
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">Annulla</button>
      </div>
    </form>
  );
}

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('job-offers');
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobOffers, setJobOffers] = useState([]);
  const [subcontractRequests, setSubcontractRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Modal states
  const [showJobModal, setShowJobModal] = useState(false);
  const [showSubcontractModal, setShowSubcontractModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editingSubcontract, setEditingSubcontract] = useState(null);
  const [viewingContact, setViewingContact] = useState(null);
  const [viewingApplication, setViewingApplication] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contactsRes, appsRes, jobsRes, subRes] = await Promise.all([
        axios.get(`${API}/contacts`),
        axios.get(`${API}/applications`),
        axios.get(`${API}/job-offers`),
        axios.get(`${API}/subcontract-requests`)
      ]);
      setContacts(contactsRes.data);
      setApplications(appsRes.data);
      setJobOffers(jobsRes.data);
      setSubcontractRequests(subRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleDeleteJob = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa offerta?')) return;
    try {
      await axios.delete(`${API}/job-offers/${id}`);
      toast.success('Offerta eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleDeleteSubcontract = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa richiesta?')) return;
    try {
      await axios.delete(`${API}/subcontract-requests/${id}`);
      toast.success('Richiesta eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;
    const headers = Object.keys(data[0]).filter(k => k !== 'id');
    const csvContent = [headers.join(';'), ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const navigate = useNavigate();
  const adminEmail = getAdminEmail();

  const handleLogout = () => {
    clearSession();
    toast.success('Logout effettuato');
    navigate('/admin/login', { replace: true });
  };

  const handleDownloadCV = async (app) => {
    try {
      const response = await axios.get(`${API}/download-cv/${app.id}`, { responseType: 'blob' });
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `cv_${app.name.replace(/\s+/g, '_')}.pdf`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore nel download del CV');
      console.error(err);
    }
  };

  const techApps = applications.filter(a => a.application_type === 'tecnici');
  const companyApps = applications.filter(a => a.application_type === 'imprese');
  const freelanceApps = applications.filter(a => a.application_type === 'freelance');

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F5F5F7]">
      <Navbar />
      <main className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <Link to="/" className="inline-flex items-center gap-2 text-[#555555] hover:text-[#1A1A2E] mb-4">
                <ArrowLeft size={20} /> Torna al sito
              </Link>
              <h1 className="text-3xl font-bold text-[#1A1A2E]">Dashboard Admin</h1>
              <p className="text-[#555555]">Gestisci offerte, richieste, contatti e candidature</p>
            </div>
            <div className="flex items-center gap-3 mt-6">
              {adminEmail && (
                <span data-testid="admin-user-email" className="text-sm text-[#555555] hidden md:inline">
                  {adminEmail}
                </span>
              )}
              <button
                onClick={handleLogout}
                data-testid="admin-logout-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1A1A2E] text-[#1A1A2E] hover:bg-[#1A1A2E] hover:text-white transition-colors font-bold text-sm"
              >
                <LogOut size={16} /> Logout
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 border-2 border-gray-200">
              <Briefcase className="w-6 h-6 text-[#27AE60] mb-1" />
              <div className="text-2xl font-bold text-[#1A1A2E]">{jobOffers.length}</div>
              <div className="text-xs text-[#888888]">Offerte lavoro</div>
            </div>
            <div className="bg-white p-4 border-2 border-gray-200">
              <Building2 className="w-6 h-6 text-[#E94560] mb-1" />
              <div className="text-2xl font-bold text-[#1A1A2E]">{subcontractRequests.length}</div>
              <div className="text-xs text-[#888888]">Subappalti</div>
            </div>
            <div className="bg-white p-4 border-2 border-gray-200">
              <Mail className="w-6 h-6 text-[#1A1A2E] mb-1" />
              <div className="text-2xl font-bold text-[#1A1A2E]">{contacts.length}</div>
              <div className="text-xs text-[#888888]">Contatti</div>
            </div>
            <div className="bg-white p-4 border-2 border-gray-200">
              <HardHat className="w-6 h-6 text-[#1A1A2E] mb-1" />
              <div className="text-2xl font-bold text-[#1A1A2E]">{techApps.length}</div>
              <div className="text-xs text-[#888888]">Tecnici</div>
            </div>
            <div className="bg-white p-4 border-2 border-gray-200">
              <Building2 className="w-6 h-6 text-[#1A1A2E] mb-1" />
              <div className="text-2xl font-bold text-[#1A1A2E]">{companyApps.length}</div>
              <div className="text-xs text-[#888888]">Imprese</div>
            </div>
            <div className="bg-white p-4 border-2 border-gray-200">
              <Users className="w-6 h-6 text-[#1A1A2E] mb-1" />
              <div className="text-2xl font-bold text-[#1A1A2E]">{freelanceApps.length}</div>
              <div className="text-xs text-[#888888]">Freelance</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { id: 'job-offers', label: 'Offerte Lavoro', icon: Briefcase, count: jobOffers.length },
              { id: 'subcontracts', label: 'Subappalti', icon: Building2, count: subcontractRequests.length },
              { id: 'contacts', label: 'Contatti', icon: Mail, count: contacts.length },
              { id: 'tecnici', label: 'Tecnici', icon: HardHat, count: techApps.length },
              { id: 'imprese', label: 'Imprese', icon: Building2, count: companyApps.length },
              { id: 'freelance', label: 'Freelance', icon: Users, count: freelanceApps.length },
            ].map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSelectedItem(null); }}
                className={`flex items-center gap-2 px-4 py-2 font-bold transition-colors ${
                  activeTab === tab.id ? 'bg-[#1A1A2E] text-white' : 'bg-white text-[#1A1A2E] hover:bg-gray-100'
                }`}>
                <tab.icon size={18} />
                {tab.label}
                <span className={`px-2 py-0.5 text-xs rounded-full ${activeTab === tab.id ? 'bg-white/20' : 'bg-[#E94560] text-white'}`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          {(activeTab === 'job-offers' || activeTab === 'subcontracts') && (
            <div className="flex gap-4 mb-6">
              {activeTab === 'job-offers' && (
                <button onClick={() => { setEditingJob(null); setShowJobModal(true); }} className="btn-primary flex items-center gap-2">
                  <Plus size={18} /> Nuova offerta
                </button>
              )}
              {activeTab === 'subcontracts' && (
                <button onClick={() => { setEditingSubcontract(null); setShowSubcontractModal(true); }} className="btn-primary flex items-center gap-2">
                  <Plus size={18} /> Nuova richiesta
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="bg-white border-2 border-gray-200">
            {loading ? (
              <div className="p-8 text-center text-[#888888]">Caricamento...</div>
            ) : (
              <>
                {/* Job Offers Tab */}
                {activeTab === 'job-offers' && (
                  <div className="divide-y divide-gray-100">
                    {jobOffers.length === 0 ? (
                      <div className="p-8 text-center text-[#888888]">Nessuna offerta di lavoro</div>
                    ) : jobOffers.map(job => (
                      <div key={job.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-bold text-[#1A1A2E]">{job.title}</h3>
                            <span className={`text-xs px-2 py-0.5 ${job.is_active ? 'bg-[#27AE60] text-white' : 'bg-gray-300 text-gray-600'}`}>
                              {job.is_active ? 'Attiva' : 'Bozza'}
                            </span>
                          </div>
                          <div className="text-sm text-[#888888]">{job.location} • {job.contract_type} • {job.experience_required}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setEditingJob(job); setShowJobModal(true); }} className="p-2 hover:bg-[#1A1A2E] hover:text-white transition-colors">
                            <Edit size={18} />
                          </button>
                          <button onClick={() => handleDeleteJob(job.id)} className="p-2 hover:bg-[#E94560] hover:text-white transition-colors">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Subcontracts Tab */}
                {activeTab === 'subcontracts' && (
                  <div className="divide-y divide-gray-100">
                    {subcontractRequests.length === 0 ? (
                      <div className="p-8 text-center text-[#888888]">Nessuna richiesta di subappalto</div>
                    ) : subcontractRequests.map(req => (
                      <div key={req.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-bold text-[#1A1A2E]">{req.title}</h3>
                            <span className={`text-xs px-2 py-0.5 ${req.is_active ? 'bg-[#27AE60] text-white' : 'bg-gray-300 text-gray-600'}`}>
                              {req.is_active ? 'Attiva' : 'Bozza'}
                            </span>
                          </div>
                          <div className="text-sm text-[#888888]">{req.project_name} • {req.location} • {req.specialization}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setEditingSubcontract(req); setShowSubcontractModal(true); }} className="p-2 hover:bg-[#1A1A2E] hover:text-white transition-colors">
                            <Edit size={18} />
                          </button>
                          <button onClick={() => handleDeleteSubcontract(req.id)} className="p-2 hover:bg-[#E94560] hover:text-white transition-colors">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Contacts Tab */}
                {activeTab === 'contacts' && (
                  <div className="divide-y divide-gray-100">
                    {contacts.length === 0 ? (
                      <div className="p-8 text-center text-[#888888]">Nessun contatto</div>
                    ) : contacts.map(c => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => setViewingContact(c)}
                        data-testid={`contact-row-${c.id}`}
                        className="w-full text-left p-4 hover:bg-gray-50 transition-colors block cursor-pointer"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-[#1A1A2E]">{c.name}</span>
                          <span className="text-xs text-[#888888]">{formatDate(c.created_at)}</span>
                        </div>
                        <div className="text-sm text-[#555555]">{c.email} {c.phone && `• ${c.phone}`}</div>
                        <p className="text-sm text-[#888888] mt-1 line-clamp-2">{c.message}</p>
                        <span className="text-xs text-[#E94560] hover:underline mt-2 inline-block">Apri dettagli →</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Applications Tabs */}
                {['tecnici', 'imprese', 'freelance'].includes(activeTab) && (() => {
                  const apps = activeTab === 'tecnici' ? techApps : activeTab === 'imprese' ? companyApps : freelanceApps;
                  return (
                    <div className="divide-y divide-gray-100">
                      {apps.length === 0 ? (
                        <div className="p-8 text-center text-[#888888]">Nessuna candidatura</div>
                      ) : apps.map(app => (
                        <div
                          key={app.id}
                          onClick={() => setViewingApplication(app)}
                          data-testid={`application-row-${app.id}`}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setViewingApplication(app); }}
                          className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-[#1A1A2E]">{app.name}</span>
                            <span className="text-xs text-[#888888]">{formatDate(app.created_at)}</span>
                          </div>
                          <div className="text-sm text-[#555555]">{app.email} • {app.phone}</div>
                          <div className="text-sm text-[#888888] mt-1">
                            {app.role || app.company_name || app.collaboration_type}
                            {app.experience_years && ` • ${app.experience_years} anni exp.`}
                          </div>
                          <div className="flex items-center gap-4 mt-2" onClick={(e) => e.stopPropagation()}>
                            <a href={`mailto:${app.email}`} className="text-sm text-[#E94560] hover:underline">Contatta</a>
                            {app.cv_url && (
                              <button
                                type="button"
                                onClick={() => handleDownloadCV(app)}
                                data-testid={`download-cv-btn-${app.id}`}
                                className="text-sm text-[#27AE60] hover:underline flex items-center gap-1"
                              >
                                <FileText size={14} /> Scarica CV
                              </button>
                            )}
                            <span className="text-xs text-[#888888] ml-auto">Apri dettagli →</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      <Modal isOpen={showJobModal} onClose={() => setShowJobModal(false)} title={editingJob ? 'Modifica offerta' : 'Nuova offerta di lavoro'}>
        <JobOfferForm offer={editingJob} onSave={() => { setShowJobModal(false); fetchData(); }} onCancel={() => setShowJobModal(false)} />
      </Modal>

      <Modal isOpen={showSubcontractModal} onClose={() => setShowSubcontractModal(false)} title={editingSubcontract ? 'Modifica richiesta' : 'Nuova richiesta di subappalto'}>
        <SubcontractForm request={editingSubcontract} onSave={() => { setShowSubcontractModal(false); fetchData(); }} onCancel={() => setShowSubcontractModal(false)} />
      </Modal>

      {/* Contact detail modal */}
      <Modal isOpen={!!viewingContact} onClose={() => setViewingContact(null)} title="Dettaglio contatto">
        {viewingContact && (
          <div data-testid="contact-detail-modal" className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Nome</div>
                <div className="text-[#1A1A2E] font-medium break-words">{viewingContact.name}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Data</div>
                <div className="text-[#1A1A2E]">{formatDate(viewingContact.created_at)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Email</div>
                <a href={`mailto:${viewingContact.email}`} className="text-[#E94560] hover:underline break-all">{viewingContact.email}</a>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Telefono</div>
                <div className="text-[#1A1A2E]">{viewingContact.phone || '—'}</div>
              </div>
              {viewingContact.company && (
                <div className="sm:col-span-2">
                  <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Azienda</div>
                  <div className="text-[#1A1A2E]">{viewingContact.company}</div>
                </div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#888888] font-bold mb-2">Messaggio completo</div>
              <div data-testid="contact-detail-message" className="bg-[#F5F5F7] border-l-4 border-[#27AE60] p-4 text-[#1A1A2E] whitespace-pre-wrap break-words leading-relaxed max-h-[50vh] overflow-y-auto">
                {viewingContact.message}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-200">
              <a
                href={`mailto:${viewingContact.email}?subject=Re: contatto SELL%26REC`}
                data-testid="contact-detail-reply"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Mail size={16} /> Rispondi via email
              </a>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(viewingContact.message); toast.success('Messaggio copiato'); }}
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                Copia messaggio
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Application detail modal */}
      <Modal isOpen={!!viewingApplication} onClose={() => setViewingApplication(null)} title="Dettaglio candidatura">
        {viewingApplication && (
          <div data-testid="application-detail-modal" className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Nome</div>
                <div className="text-[#1A1A2E] font-medium break-words">{viewingApplication.name}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Categoria</div>
                <div className="text-[#1A1A2E] capitalize">{viewingApplication.application_type}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Email</div>
                <a href={`mailto:${viewingApplication.email}`} className="text-[#E94560] hover:underline break-all">{viewingApplication.email}</a>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Telefono</div>
                <div className="text-[#1A1A2E]">{viewingApplication.phone || '—'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Data invio</div>
                <div className="text-[#1A1A2E]">{formatDate(viewingApplication.created_at)}</div>
              </div>
              {viewingApplication.role && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Ruolo</div>
                  <div className="text-[#1A1A2E]">{viewingApplication.role}</div>
                </div>
              )}
              {viewingApplication.experience_years != null && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Esperienza</div>
                  <div className="text-[#1A1A2E]">{viewingApplication.experience_years} anni</div>
                </div>
              )}
              {viewingApplication.availability && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Disponibilità</div>
                  <div className="text-[#1A1A2E]">{viewingApplication.availability}</div>
                </div>
              )}
              {viewingApplication.company_name && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Azienda</div>
                  <div className="text-[#1A1A2E]">{viewingApplication.company_name}</div>
                </div>
              )}
              {viewingApplication.specialization && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Specializzazione</div>
                  <div className="text-[#1A1A2E]">{viewingApplication.specialization}</div>
                </div>
              )}
              {viewingApplication.certifications && (
                <div className="sm:col-span-2">
                  <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Certificazioni</div>
                  <div className="text-[#1A1A2E]">{viewingApplication.certifications}</div>
                </div>
              )}
              {viewingApplication.collaboration_type && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#888888] font-bold">Collaborazione</div>
                  <div className="text-[#1A1A2E]">{viewingApplication.collaboration_type}</div>
                </div>
              )}
            </div>
            {viewingApplication.notes && (
              <div>
                <div className="text-xs uppercase tracking-wider text-[#888888] font-bold mb-2">Note del candidato</div>
                <div data-testid="application-detail-notes" className="bg-[#F5F5F7] border-l-4 border-[#27AE60] p-4 text-[#1A1A2E] whitespace-pre-wrap break-words leading-relaxed max-h-[40vh] overflow-y-auto">
                  {viewingApplication.notes}
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-200">
              <a
                href={`mailto:${viewingApplication.email}?subject=Re: candidatura SELL%26REC`}
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Mail size={16} /> Contatta candidato
              </a>
              {viewingApplication.cv_url && (
                <button
                  type="button"
                  onClick={() => handleDownloadCV(viewingApplication)}
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <FileText size={16} /> Scarica CV
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
